/**
 * 휴지통 페이지 (/calendar/trash, 2026-09-18): 복원 · 영구 삭제 · 비우기 · 시리즈 전체 복원.
 *
 * 전부 fetch 로 보내고 행만 갈아끼운다 — 실행 취소 스택 밖이다(영구 삭제는 되돌릴
 * 수 없고, 복원은 표에서 사라지는 것이 곧 결과다). 영구 삭제·비우기는 appConfirm 을
 * 지난다. JS 없이는 표만 보인다(버튼은 아무것도 하지 않는다).
 * 문구는 #calTrash 의 data-msg-* / data-label-* 에서 온다.
 *
 * 다른 탭에서 이미 비웠거나 되살린 일정(404, AZ8)은 실패가 아니라 "이미 없다" 고 말하고
 * 표를 서버의 지금 모습으로 다시 읽는다 — 행·건수·빈 상태·비우기 버튼만 갈아끼우므로
 * 클릭 위임(#calTrash)은 그대로 산다.
 */
import { api } from './calendar-history.js';
import { appConfirm } from './confirm-dialog.js';
import { navigate } from './calendar-spa.js';

const fmt = (s, v) => String(s || '').replace(/%[sd]/, String(v));
const csrf = () => (window.getCSRFToken ? window.getCSRFToken() : '');

// 「시리즈 전체 복원」 (BA5 d): POST /calendar/series/:id/restore → {restored}
function restoreSeries(id) {
    const body = new URLSearchParams();
    body.set('_csrf', csrf());
    return fetch('/calendar/series/' + encodeURIComponent(id) + '/restore', {
        method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'application/json' },
    }).then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
    });
}

export function setupCalendarTrash() {
    const root = document.getElementById('calTrash');
    if (!root || root.dataset.bound) return;
    root.dataset.bound = '1';
    const L = root.dataset;
    // 표·건수·빈 상태·비우기 버튼은 "다시 읽기" 가 갈아끼우므로 그때그때 찾는다.
    const $ = (id) => document.getElementById(id);
    const rows = () => Array.from(root.querySelectorAll('tr[data-id]'));
    const toast = (msg, isError) => {
        if (window.showNotification) window.showNotification(msg, isError ? 'error' : 'success');
    };
    function paintCount() {
        const n = rows().length;
        const count = $('calTrashCount');
        if (count) {
            count.textContent = fmt(L.labelCount, n);
            count.dataset.count = String(n);
        }
        // 인스턴스가 모두 빠진 시리즈 머리 행도 치운다
        root.querySelectorAll('tr.calt-series-head').forEach((head) => {
            if (!root.querySelector('tr[data-id][data-series="' + CSS.escape(head.dataset.series) + '"]')) head.remove();
        });
        if (n === 0) {
            const table = $('calTrashTable');
            const emptyState = $('calTrashEmptyState');
            const emptyBtn = $('calTrashEmpty');
            if (table) table.hidden = true;
            if (emptyState) emptyState.hidden = false;
            if (emptyBtn) emptyBtn.disabled = true;
        }
    }
    // "이미 없다" (404): 서버가 지금 가진 휴지통으로 표를 다시 읽는다 (AZ8)
    function reloadTable() {
        return navigate(window.location.href, { swap: '#calTrashTable, #calTrashCount, #calTrashEmptyState, #calTrashEmpty' }).catch(() => {});
    }
    const gone = (err) => /HTTP 404/.test(String(err && err.message));

    root.addEventListener('click', async (e) => {
        const seriesBtn = e.target.closest('.cal-trash-series-restore');
        if (seriesBtn) {
            const head = seriesBtn.closest('tr.calt-series-head');
            if (!head) return;
            head.setAttribute('data-busy', '1');
            try {
                const r = await restoreSeries(head.dataset.series);
                // 되살린 것은 머리 행이 센 것뿐이다 (XA45) — 따로 지운 인스턴스는 남으므로 행을 지우고 표를 다시 읽는다
                const back = new Set((r && r.ids) || []);
                head.remove();
                root.querySelectorAll('tr[data-id]').forEach((tr) => { if (back.has(tr.dataset.id)) tr.remove(); });
                paintCount();
                toast(fmt(L.msgSeriesRestored, (r && r.restored) || 0));
                reloadTable();
            } catch (err) {
                head.removeAttribute('data-busy');
                if (gone(err)) { toast(L.msgGone); reloadTable(); } else toast(L.msgFailed, true);
            }
            return;
        }
        const restoreBtn = e.target.closest('.cal-trash-restore');
        const purgeBtn = e.target.closest('.cal-trash-purge');
        const tr = e.target.closest('tr[data-id]');
        if ((restoreBtn || purgeBtn) && tr) {
            const id = tr.dataset.id;
            if (purgeBtn) {
                const ok = await appConfirm({
                    message: fmt(L.msgConfirmPurge, tr.dataset.title || ''),
                    okLabel: L.labelPurge,
                    cancelLabel: L.labelCancel,
                    danger: true,
                });
                if (!ok) return;
            }
            tr.setAttribute('data-busy', '1');
            try {
                if (restoreBtn) await api.restore(id);
                else await api.purge(id);
                tr.remove();
                paintCount();
                toast(restoreBtn ? L.msgRestored : L.msgPurged);
            } catch (err) {
                tr.removeAttribute('data-busy');
                if (gone(err)) { toast(L.msgGone); reloadTable(); } else toast(L.msgFailed, true);
            }
            return;
        }
        if (e.target.closest('#calTrashEmpty')) {
            const n = rows().length;
            if (!n) return;
            const ok = await appConfirm({
                message: fmt(L.msgConfirmEmpty, n),
                okLabel: L.labelEmpty,
                cancelLabel: L.labelCancel,
                danger: true,
            });
            if (!ok) return;
            const emptyBtn = $('calTrashEmpty');
            if (emptyBtn) emptyBtn.disabled = true;
            try {
                await api.emptyTrash(L.cal || '');
                rows().forEach((row) => row.remove());
                paintCount();
                toast(L.msgEmptied);
            } catch (_) {
                if (emptyBtn) emptyBtn.disabled = false;
                toast(L.msgFailed, true);
            }
        }
    });
}
